import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { cn } from '@/lib/cn';
import { Button, estiloDeBoton } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { InputSuelto, SelectSuelto } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  useAccionDeUsuario, useRestablecerClave, useUsuarios,
  type EstadoDeUsuario, type Usuario,
} from './api';
import { FormularioDeUsuario } from './FormularioDeUsuario';
import { ModulosDeUsuario } from './ModulosDeUsuario';
import { ClaveGenerada, useBusquedaDemorada } from './piezas';
import type { IrA } from './Administracion';

/**
 * Usuarios — el alta, la edición y los permisos de cada persona.
 *
 * ## Lo que cambia
 *
 * - **El nombre ya no viaja dentro de un `onclick`** con las comillas simples
 *   quitadas (`u.full_name.replace(/'/g,"")`, tres veces). Un nombre con
 *   comillas dobles rompía el manejador.
 * - **La contraseña temporal se puede copiar.** Salía en un modal como texto
 *   suelto; ahora tiene su botón, porque es lo único que se hace con ella.
 * - **Buscar y filtrar por estado no se pisan.** El vanilla releía el filtro
 *   desde el DOM en cada tecla.
 * - **Si la lista falla, se dice qué falló.** Era un `catch` que pintaba
 *   «Error cargando usuarios» sin distinguir un 403 de un 503.
 */
export function Usuarios({ fichaInicial = null, onIrA }: {
  fichaInicial?: number | null;
  /** Para el botón «Importar», que lleva a la sección de Importaciones. */
  onIrA?: IrA;
} = {}) {
  const [texto, setTexto] = useState('');
  const busqueda = useBusquedaDemorada(texto);
  const [estado, setEstado] = useState<EstadoDeUsuario>('activos');

  const consulta = useUsuarios(busqueda, estado, true);
  const activar = useAccionDeUsuario('activate');
  const inactivar = useAccionDeUsuario('deactivate');
  const reactivar = useAccionDeUsuario('reactivate');
  const restablecer = useRestablecerClave();

  // `fichaInicial` llega desde Calidad de datos: sus hallazgos nombran a una
  // persona concreta y hay que abrir SU ficha, no dejar la lista y que la busque
  // quien ya sabe de quién se trata.
  const [editando, setEditando] = useState<number | null | undefined>(
    fichaInicial ?? undefined);
  const [altaRapida, setAltaRapida] = useState(false);
  const [modulosDe, setModulosDe] = useState<Usuario | null>(null);
  const [clave, setClave] = useState<{ titulo: string; pass: string; nota: string } | null>(null);
  const [confirmando, setConfirmando] = useState<
    { usuario: Usuario; accion: 'inactivar' | 'clave' } | null>(null);

  const usuarios = consulta.data?.items ?? [];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <InputSuelto
          type="search"
          aria-label="Buscar usuario"
          className="min-w-48 flex-1"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar por nombre o correo…"
        />
        <SelectSuelto
          aria-label="Estado"
          className="w-auto"
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoDeUsuario)}
        >
          <option value="activos">Activos</option>
          <option value="inactivos">Inactivos</option>
          <option value="todos">Todos</option>
        </SelectSuelto>
        {/* «Importar» llevaba a un diálogo propio contra `/admin/users/bulk-import`:
            subía el Excel y aplicaba, sin vista previa y sin validar contra los
            catálogos. El motor de Importaciones —la sección de al lado— hace lo
            mismo con vista previa, validación y actualización no destructiva, y
            «Colaboradores» es su único destino. Así que el botón lleva ahí en vez
            de mantener dos formas de importar lo mismo, una peor. */}
        <Button variant="secondary" size="sm" onClick={() => onIrA?.('imports')}>
          <i className="bi bi-upload" aria-hidden="true" /> Importar
        </Button>
        <a
          href="#directorio/fotos"
          className={cn(estiloDeBoton({ variant: 'secondary', size: 'sm' }), 'no-underline')}
          title="Cargar fotos por cédula, en un .zip"
        >
          <i className="bi bi-images" aria-hidden="true" /> Fotos masivas
        </a>
        <Button variant="secondary" size="sm" onClick={() => setAltaRapida(true)}>
          <i className="bi bi-person-plus-fill" aria-hidden="true" /> Alta rápida
        </Button>
        <Button size="sm" onClick={() => setEditando(null)}>
          <i className="bi bi-person-plus" aria-hidden="true" /> Crear usuario
        </Button>
      </div>

      {consulta.isPending ? (
        <SkeletonFilas filas={8} columnas={5} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="los usuarios"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !usuarios.length ? (
        <EstadoVacio
          icono={busqueda ? 'bi-search' : 'bi-people'}
          titulo={busqueda ? 'Sin resultados' : 'Sin usuarios'}
          descripcion={busqueda ? `Nada coincide con «${busqueda}».` : undefined}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full border-collapse text-3">
            <thead>
              <tr className="border-b border-line bg-sunken text-left">
                {['Nombre', 'Cédula', 'Correo', 'Área', 'Rol', 'Estado'].map((h) => (
                  <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 font-semibold text-brand-ink">
                    {h}
                  </th>
                ))}
                <th scope="col" className="px-3 py-2"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} className="border-b border-line last:border-0 hover:bg-sunken">
                  <td className="px-3 py-2 font-semibold text-ink">{u.full_name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-ink-2">{u.cedula}</td>
                  <td className="px-3 py-2 text-ink-2">{u.email}</td>
                  <td className="px-3 py-2 text-ink-2">{u.area_name || '—'}</td>
                  <td className="px-3 py-2"><Badge tone="info">{u.role}</Badge></td>
                  <td className="px-3 py-2">
                    {u.status === 'preregistro' ? (
                      <Badge tone="warn" title="Falta completar la información administrativa">
                        <i className="bi bi-hourglass-split" aria-hidden="true" /> Preregistro
                      </Badge>
                    ) : (
                      <Badge tone={u.is_active ? 'ok' : 'neutral'}>
                        {u.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">
                    {u.status === 'preregistro' && (
                      <Button
                        variant="ghost" size="sm" iconOnly
                        aria-label={`Completar y activar a ${u.full_name}`}
                        onClick={() => activar.mutate(u.id, {
                          onSuccess: (r) => Utils.toast(r.message, 'success'),
                          // Activar falla cuando falta información: eso no es un
                          // error, es el siguiente paso.
                          onError: () => setEditando(u.id),
                        })}
                      >
                        <i className="bi bi-check2-circle text-ok-ink" aria-hidden="true" />
                      </Button>
                    )}
                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Editar a ${u.full_name}`}
                      onClick={() => setEditando(u.id)}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Módulos de ${u.full_name}`}
                      onClick={() => setModulosDe(u)}
                    >
                      <i className="bi bi-key" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" iconOnly
                      aria-label={`Restablecer la contraseña de ${u.full_name}`}
                      onClick={() => setConfirmando({ usuario: u, accion: 'clave' })}
                    >
                      <i className="bi bi-shield-lock" aria-hidden="true" />
                    </Button>
                    {u.is_active ? (
                      <Button
                        variant="ghost" size="sm" iconOnly
                        aria-label={`Inactivar a ${u.full_name}`}
                        onClick={() => setConfirmando({ usuario: u, accion: 'inactivar' })}
                      >
                        <i className="bi bi-pause-circle" aria-hidden="true" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost" size="sm" iconOnly
                        aria-label={`Reactivar a ${u.full_name}`}
                        onClick={() => reactivar.mutate(u.id, {
                          onSuccess: (r) => Utils.toast(r.message, 'success'),
                          onError: (err) => Utils.toast(
                            err instanceof Error ? err.message : 'No se pudo reactivar', 'error'),
                        })}
                      >
                        <i className="bi bi-play-circle text-ok-ink" aria-hidden="true" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(editando !== undefined || altaRapida) && (
        <FormularioDeUsuario
          id={altaRapida ? null : editando ?? null}
          rapida={altaRapida}
          onCerrar={() => { setEditando(undefined); setAltaRapida(false); }}
          onClave={(pass, titulo, nota) => setClave({ pass, titulo, nota })}
          onCompletar={(id) => setEditando(id)}
        />
      )}

      {modulosDe && (
        <ModulosDeUsuario usuario={modulosDe} onCerrar={() => setModulosDe(null)} />
      )}

      {clave && (
        <ClaveGenerada
          titulo={clave.titulo}
          clave={clave.pass}
          nota={clave.nota}
          onCerrar={() => setClave(null)}
        />
      )}

      <Dialog
        abierto={confirmando != null}
        onCambio={(v) => { if (!v) setConfirmando(null); }}
        titulo={confirmando?.accion === 'clave'
          ? 'Restablecer la contraseña'
          : 'Inactivar al colaborador'}
        descripcion={confirmando?.accion === 'clave'
          ? `Se genera una contraseña temporal para ${confirmando?.usuario.full_name}. La actual deja de servir en ese momento, así que hay que comunicársela.`
          : `${confirmando?.usuario.full_name} deja de poder entrar y de listarse. Su ficha, su expediente y su historial se conservan.`}
        ancho="max-w-md"
        pie={
          <>
            <Button variant="ghost" onClick={() => setConfirmando(null)} autoFocus>Cancelar</Button>
            <Button
              variant={confirmando?.accion === 'inactivar' ? 'destructive' : 'primary'}
              loading={restablecer.isPending || inactivar.isPending}
              onClick={() => {
                if (!confirmando) return;
                const { usuario, accion } = confirmando;
                if (accion === 'clave') {
                  restablecer.mutate(usuario.id, {
                    onSuccess: (r) => {
                      setConfirmando(null);
                      setClave({
                        titulo: 'Contraseña restablecida',
                        pass: r.temp_password,
                        nota: `Comunícasela a ${usuario.full_name}. Tendrá que cambiarla al entrar.`,
                      });
                    },
                    onError: (err) => {
                      setConfirmando(null);
                      Utils.toast(err instanceof Error ? err.message : 'No se pudo', 'error');
                    },
                  });
                } else {
                  inactivar.mutate(usuario.id, {
                    onSuccess: (r) => { setConfirmando(null); Utils.toast(r.message, 'success'); },
                    onError: (err) => {
                      setConfirmando(null);
                      Utils.toast(err instanceof Error ? err.message : 'No se pudo', 'error');
                    },
                  });
                }
              }}
            >
              {confirmando?.accion === 'clave' ? 'Restablecer' : 'Inactivar'}
            </Button>
          </>
        }
      />
    </>
  );
}
